import type { ItemIndex } from '../../doc/ItemIndex';
import { rotatedBounds, type Rect } from '../../doc/geometry';
import type { Geometry } from '../../doc/schema';
import type { Handle } from './transforms';

/** Vertical (x) and horizontal (y) lines other items offer as snap targets, in world units. */
export interface SnapCandidates {
  xs: number[];
  ys: number[];
}

export interface SnapResult {
  dx: number;
  dy: number;
  /** Matched guide positions for drawing, or null when no snap happened on that axis. */
  gx: number | null;
  gy: number | null;
}

/** Edges and centres of every unlocked item intersecting `area`, except the excluded ones. */
export function collectCandidates(index: ItemIndex, area: Rect, exclude: ReadonlySet<string>): SnapCandidates {
  const xs = new Set<number>();
  const ys = new Set<number>();
  for (const item of index.query(area)) {
    if (exclude.has(item.id)) continue;
    const b = rotatedBounds(item);
    xs.add(b.x);
    xs.add(b.x + b.w / 2);
    xs.add(b.x + b.w);
    ys.add(b.y);
    ys.add(b.y + b.h / 2);
    ys.add(b.y + b.h);
  }
  return { xs: [...xs].sort((a, b) => a - b), ys: [...ys].sort((a, b) => a - b) };
}

function best(edges: number[], cands: number[], threshold: number): { delta: number; at: number } | null {
  let out: { delta: number; at: number } | null = null;
  for (const e of edges) {
    for (const c of cands) {
      const d = c - e;
      if (Math.abs(d) <= threshold && (!out || Math.abs(d) < Math.abs(out.delta))) out = { delta: d, at: c };
    }
  }
  return out;
}

/**
 * Snap a moving rect (its left/centre/right and top/centre/bottom) to candidate lines and,
 * optionally, its top-left to a grid. Returns the correction to add to the rect.
 */
export function snapRect(rect: Rect, cands: SnapCandidates, threshold: number, grid: number | null): SnapResult {
  const res: SnapResult = { dx: 0, dy: 0, gx: null, gy: null };
  const bx = best([rect.x, rect.x + rect.w / 2, rect.x + rect.w], cands.xs, threshold);
  const by = best([rect.y, rect.y + rect.h / 2, rect.y + rect.h], cands.ys, threshold);
  if (bx) {
    res.dx = bx.delta;
    res.gx = bx.at;
  } else if (grid) {
    const g = Math.round(rect.x / grid) * grid - rect.x;
    if (Math.abs(g) <= threshold) res.dx = g;
  }
  if (by) {
    res.dy = by.delta;
    res.gy = by.at;
  } else if (grid) {
    const g = Math.round(rect.y / grid) * grid - rect.y;
    if (Math.abs(g) <= threshold) res.dy = g;
  }
  return res;
}

/** Snap a single edge position (used while resizing). Returns the corrected position and guide. */
export function snapEdge(pos: number, cands: number[], threshold: number, grid: number | null): { pos: number; guide: number | null } {
  const b = best([pos], cands, threshold);
  if (b) return { pos: b.at, guide: b.at };
  if (grid) {
    const g = Math.round(pos / grid) * grid;
    if (Math.abs(g - pos) <= threshold) return { pos: g, guide: null };
  }
  return { pos, guide: null };
}

/** Snap threshold in world units for the given zoom (8 screen pixels). */
export const snapThreshold = (zoom: number): number => 8 / zoom;

export interface SnapGeometry {
  geometry: Geometry;
  gx: number | null;
  gy: number | null;
}

/**
 * Snap the dragged edges of a resized (unrotated) box. Free resizes snap each dragged edge on
 * its own; aspect-locked resizes snap whichever dragged edge is closest to a line and scale the
 * other dimension to match, keeping the opposite side (or centre, for side handles) fixed.
 */
export function snapResize(
  handle: Handle,
  g: Geometry,
  cands: SnapCandidates,
  threshold: number,
  grid: number | null,
  keepAspect: boolean,
  minSize: number,
): SnapGeometry {
  const out: SnapGeometry = { geometry: { ...g }, gx: null, gy: null };
  const r = out.geometry;
  const edgeX = handle.sx === 1 ? g.x + g.w : g.x;
  const edgeY = handle.sy === 1 ? g.y + g.h : g.y;
  const sx = handle.sx ? snapEdge(edgeX, cands.xs, threshold, grid) : null;
  const sy = handle.sy ? snapEdge(edgeY, cands.ys, threshold, grid) : null;
  const hitX = sx && sx.pos !== edgeX ? sx : null;
  const hitY = sy && sy.pos !== edgeY ? sy : null;

  if (!keepAspect) {
    if (hitX) {
      if (handle.sx === 1) r.w = Math.max(minSize, hitX.pos - r.x);
      else {
        const right = r.x + r.w;
        r.x = Math.min(hitX.pos, right - minSize);
        r.w = right - r.x;
      }
      out.gx = hitX.guide;
    }
    if (hitY) {
      if (handle.sy === 1) r.h = Math.max(minSize, hitY.pos - r.y);
      else {
        const bottom = r.y + r.h;
        r.y = Math.min(hitY.pos, bottom - minSize);
        r.h = bottom - r.y;
      }
      out.gy = hitY.guide;
    }
    return out;
  }

  // aspect locked: take the closer of the two candidate edges and scale uniformly
  const dX = hitX ? Math.abs(hitX.pos - edgeX) : Infinity;
  const dY = hitY ? Math.abs(hitY.pos - edgeY) : Infinity;
  if (dX === Infinity && dY === Infinity) return out;
  let scale: number;
  if (dX <= dY && hitX) {
    const w = handle.sx === 1 ? hitX.pos - g.x : g.x + g.w - hitX.pos;
    scale = w / g.w;
    out.gx = hitX.guide;
  } else if (hitY) {
    const h = handle.sy === 1 ? hitY.pos - g.y : g.y + g.h - hitY.pos;
    scale = h / g.h;
    out.gy = hitY.guide;
  } else {
    return out;
  }
  const w = g.w * scale;
  const h = g.h * scale;
  if (!(w >= minSize && h >= minSize)) return { geometry: { ...g }, gx: null, gy: null };
  // anchor: the side opposite the handle, or the centre on an axis the handle does not drag
  r.x = handle.sx === 1 ? g.x : handle.sx === -1 ? g.x + g.w - w : g.x + g.w / 2 - w / 2;
  r.y = handle.sy === 1 ? g.y : handle.sy === -1 ? g.y + g.h - h : g.y + g.h / 2 - h / 2;
  r.w = w;
  r.h = h;
  return out;
}

/**
 * Snap a uniform group scale: the dragged corner of the scaled bounding box snaps to the closest
 * line on either axis. Returns the corrected pointer to feed back into scaleGroup, or null.
 */
export function snapScalePointer(
  bbox: Rect,
  handle: Handle,
  scale: number,
  cands: SnapCandidates,
  threshold: number,
  grid: number | null,
): { pointer: { x: number; y: number }; gx: number | null; gy: number | null } | null {
  const ax = handle.sx === 1 ? bbox.x : bbox.x + bbox.w;
  const ay = handle.sy === 1 ? bbox.y : bbox.y + bbox.h;
  const edgeX = ax + handle.sx * bbox.w * scale;
  const edgeY = ay + handle.sy * bbox.h * scale;
  const sx = snapEdge(edgeX, cands.xs, threshold, grid);
  const sy = snapEdge(edgeY, cands.ys, threshold, grid);
  const dX = sx.pos !== edgeX ? Math.abs(sx.pos - edgeX) : Infinity;
  const dY = sy.pos !== edgeY ? Math.abs(sy.pos - edgeY) : Infinity;
  if (dX === Infinity && dY === Infinity) return null;
  let s: number;
  let gx: number | null = null;
  let gy: number | null = null;
  if (dX <= dY) {
    s = bbox.w > 0 ? ((sx.pos - ax) * handle.sx) / bbox.w : scale;
    gx = sx.guide;
  } else {
    s = bbox.h > 0 ? ((sy.pos - ay) * handle.sy) / bbox.h : scale;
    gy = sy.guide;
  }
  if (!(s > 0)) return null;
  return { pointer: { x: ax + handle.sx * bbox.w * s, y: ay + handle.sy * bbox.h * s }, gx, gy };
}
